/**
 * PWA "Add to Home Screen" install banner (Iter 67do).
 *
 * Two modes:
 *  1. Android / Chromium browsers — listens for the `beforeinstallprompt`
 *     event and shows a slim banner with an "Install" button that calls
 *     the deferred prompt. Once accepted (or the user installs via the
 *     browser menu) the `appinstalled` event hides + remembers the banner
 *     forever.
 *  2. iOS Safari (which doesn't expose `beforeinstallprompt`) — detects
 *     `isStandalone === false` + iOS UA and shows a guidance line
 *     ("Tap the share icon, then ‘Add to Home Screen’").
 *
 * Dismissal is persisted to `localStorage` so the banner doesn't nag.
 * The user can re-trigger it later by clearing site data or by visiting
 * the future Profile → Settings → "Install app" entry.
 */
import { useEffect, useState } from "react";
import { Share, Plus, Download, X } from "lucide-react";
import { isEmbedSurface } from "@/lib/isEmbedSurface";

const DISMISS_KEY = "rentalcar:pwa-install-dismissed";

function _isStandalone() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  // iOS Safari quirk — exposes `navigator.standalone`
  if (window.navigator?.standalone === true) return true;
  return false;
}

function _isIosSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOS = /iPhone|iPad|iPod/i.test(ua);
  const webkit = /WebKit/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
  return iOS && webkit;
}

export default function PWAInstallBanner() {
  // Deferred install prompt (Chrome/Android). null until the event fires.
  const [deferred, setDeferred] = useState(null);
  // iOS guidance mode (no programmatic install — show instructions).
  const [iosMode, setIosMode] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (_isStandalone()) return;
    // Iter 69 — never inside /embed/* or any iframe context.
    if (isEmbedSurface()) return;
    try {
      if (localStorage.getItem(DISMISS_KEY)) return;
    } catch { /* ignore */ }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    const onInstalled = () => {
      try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // iOS fallback — show after a short delay so it doesn't fight the
    // initial paint / cookie banner.
    if (_isIosSafari()) {
      const t = setTimeout(() => { setIosMode(true); setVisible(true); }, 4500);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") {
      try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    }
    setDeferred(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:w-80 z-[60] rounded-2xl bg-neutral-900 text-white shadow-2xl ring-1 ring-amber-500/30 overflow-hidden"
      data-testid="pwa-install-banner"
    >
      <div className="flex items-start gap-3 p-3.5">
        <div className="h-10 w-10 rounded-xl bg-amber-500 flex items-center justify-center shrink-0 ring-1 ring-amber-700/30 shadow-md shadow-amber-500/20">
          <img src="/logo.svg" alt="" className="h-9 w-9 rounded-lg" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold leading-tight">
            Add RentalCar<span className="text-red-500">.al</span> to your home screen
          </div>
          {iosMode ? (
            <div className="mt-1 text-[12px] leading-snug text-white/80 flex flex-wrap items-center gap-1.5" data-testid="pwa-install-ios-hint">
              Tap <Share className="h-3 w-3 text-amber-300 inline" aria-label="Share icon" /> then
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-white/10 ring-1 ring-white/15 text-[10px] font-semibold">
                <Plus className="h-2.5 w-2.5" /> Add to Home Screen
              </span>
              for one-tap booking.
            </div>
          ) : (
            <div className="mt-1 text-[12px] leading-snug text-white/80">
              Faster bookings, offline access to your trips, and a dedicated icon on your phone.
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss install banner"
          className="h-6 w-6 rounded-full bg-white/5 hover:bg-white/15 text-white/60 hover:text-white flex items-center justify-center transition shrink-0"
          data-testid="pwa-install-dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {!iosMode && deferred && (
        <div className="px-3.5 pb-3.5">
          <button
            type="button"
            onClick={install}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-neutral-900 font-bold text-sm h-10 transition shadow-md shadow-amber-500/20"
            data-testid="pwa-install-accept"
          >
            <Download className="h-4 w-4" /> Install app
          </button>
        </div>
      )}
    </div>
  );
}
